/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Box, Grid, Stack } from "@mui/material";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import BadgeIcon from "@mui/icons-material/Badge";
import useHttp from "hooks/http.hook";
import { FormProps } from "interfaces";
import {
  ResourceInterface,
  HeaderEnum,
  ProviderInterface,
  QueryResultInterface,
  FacilityInterface,
} from "@malmur-technologies/medicalsystem-shared";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AutoCompleteOption, EntityPermissions, FormActionType, PermissionsUser } from "types";
import { resourceFormYUPValidation, validatePhoneInputSymbol } from "utils/form.utils";
import Autocomplete from "components/generals/Autocomplete";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import FormField from "components/generals/FormInput";
import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import FormPhoneInput from "components/generals/FormPhoneInput";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";

/**
 * Resource form component for managing medical facility resources (rooms, equipment).
 * 
 * Features:
 * - Create/edit resource records with complete details
 * - Resource name and identification
 * - Default provider assignment (doctor/nurse)
 * - Facility/location assignment
 * - Resource type specification
 * - Contact phone number (optional)
 * - Autocomplete selection for providers and facilities
 * - Sorted provider and facility lists alphabetically by name
 * - Phone validation and formatting
 * - Permission-based access control (read-only for patients/doctors)
 * - Form validation with Yup schema
 * - Automatic empty field cleanup before submission
 * 
 * Resource Types:
 * - Examination rooms
 * - Operating rooms
 * - Medical equipment
 * - Diagnostic devices
 * - Treatment stations
 * 
 * Provider Assignment:
 * - Assign default provider to resource
 * - Useful for room assignments and equipment ownership
 * - Loads all active providers on form initialization
 * 
 * Facility Assignment:
 * - Link resource to specific facility/location
 * - Supports multi-facility organizations
 * - Loads all facilities on form initialization
 * 
 * Security:
 * - Permission-based rendering via PermissionWrapper
 * - Read-only mode for patients and doctors
 * - Validates entity permissions for edit/delete
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Phone sanitization to prevent injection
 * 
 * @param {FormProps<ResourceInterface>} props - Form properties
 * @param {ResourceInterface} props.model - Resource data to edit or empty model for new
 * @param {FormActionType} props.action - Form action (NEW, MODIFY, READ_ONLY)
 * @param {Function} props.onCloseAction - Callback when form closes
 * @param {Function} props.setModelList - Callback to update resource list
 * @param {ResourceInterface[]} props.modelList - Current list of resources
 * 
 * @returns {JSX.Element} Resource form with provider and facility assignment
 */
const ResourceForm: React.FC<FormProps<ResourceInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const { t } = useTranslation();
  const [doctorList, setDoctorList] = useState<ProviderInterface[]>([]);
  const [facilityList, setFacilityList] = useState<FacilityInterface[]>([]);

  const isPatientOrDoctor = useSelector(selectIsPatientOrDoctor);
  const isReadOnlyMode = useMemo(() => {
    return action === FormActionType.READ_ONLY || isPatientOrDoctor;
  }, [action, isPatientOrDoctor]);

  useEffect(() => {
    sendRequest(CoreAPI_URLs.DOCTOR_GET_ALL, onGetDoctorsHandler, "GET");
    sendRequest(CoreAPI_URLs.FACILITY_GET_ALL, onGetFacilityHandler, "GET");
  }, []);

  const submitHandler = (model: Partial<ResourceInterface>) => {
    if (isReadOnlyMode) return;
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    const successMessageTest = action === FormActionType.NEW ? t("resourceForm.resourceCreated") : t("resourceForm.resourceUpdated");

    // Handle phone field properly to avoid unintended data loss
    if (model.phone !== undefined && model.phone !== null) {
      const trimmedPhone = model.phone.trim();
      if (trimmedPhone.length === 0) {
        // User explicitly cleared the field - send null on MODIFY to clear, delete on NEW
        if (action === FormActionType.MODIFY) {
          model.phone = null;
        } else {
          delete model["phone"];
        }
      } else if (trimmedPhone.length < 3) {
        // Phone is too short (partial entry) - omit field to keep existing value on MODIFY
        // Validation should prevent submission, but if it reaches here, don't send invalid data
        delete model["phone"];
      } else {
        // Valid phone - format and send
        model.phone = validatePhoneInputSymbol(trimmedPhone);
      }
    } else if (action === FormActionType.NEW) {
      delete model["phone"];
    }

    if (!model.defaultAppointmentProvider?.id) {
      delete model["defaultAppointmentProvider"];
    }

    if (!model.location?.id) {
      delete model["location"];
    }
    sendRequest(
      CoreAPI_URLs.RESOURCE_GENERAL_OPERATIONS,
      onResourceUpdateHandler,
      method,
      JSON.stringify(model),
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
      { successMessage: successMessageTest }
    );
  };

  const onGetDoctorsHandler = (data: QueryResultInterface<ProviderInterface>) => {
    const sortedDoctors = data.results.sort((a, b) => a.person.name.localeCompare(b.person.name));
    setDoctorList(sortedDoctors);
  };

  const onGetFacilityHandler = (data: QueryResultInterface<FacilityInterface>) => {
    const sortedFacility = data.results.sort((a, b) => a.name.localeCompare(b.name));
    setFacilityList(sortedFacility);
  };

  const doctorOptions = useMemo<AutoCompleteOption[]>(() => {
    if (doctorList.length === 0) {
      return [];
    } else {
      return doctorList
        .map((a) => ({
          label: `${a.person.name} ${a.person.lastname}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [doctorList]);

  const facilityOptions = useMemo<AutoCompleteOption[]>(() => {
    if (facilityList.length === 0) {
      return [];
    } else {
      return facilityList
        .map((a) => ({
          label: `${a.name}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [facilityList]);

  const onResourceUpdateHandler = async (resource: ResourceInterface) => {
    if (resource.defaultAppointmentProvider) {
      const personData = await sendRequest(
        CoreAPI_URLs.PROVIDER_GENERAL_OPERATIONS,
        null,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: resource.defaultAppointmentProvider.id }]
      );

      resource.defaultAppointmentProvider = personData.data;
    }

    if (resource.location?.id) {
      const locationData = await sendRequest(
        CoreAPI_URLs.FACILITY_GENERAL_OPERATIONS,
        null,
        "GET",
        null,
        [{ header: HeaderEnum.RESOURCE_ID, value: resource.location.id }]
      );

      resource.location = locationData.data;
    }

    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === resource.id ? resource : model)));
    } else {
      setModelList((oldArray) => [...oldArray, resource]);
    }

    onCloseAction();
  };

  return (
    <Box sx={{ maxWidth: '60%', mx: 'auto', px: 2 }}>
      <FormikForm
        initialValues={model}
        onSubmit={(model) => {
          submitHandler(model);
        }}
        validationSchema={resourceFormYUPValidation(t)}
      >
        {({ values, errors, touched, setFieldValue }: FormikProps<Partial<ResourceInterface>>) => (
          <Form id="resourceForm" autoComplete="true">
            <Stack alignItems="center" direction="row" gap={2} mb={3}>
              <BadgeIcon fontSize="large" />
              <H4>{t("resourceForm.resourceFormTitle")}</H4>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={6}>
                <FormField
                  type="text"
                  label={t("resourceForm.name")}
                  name="name"
                  value={values.name || ""}
                  error={errors.name && touched.name}
                  readOnly={isReadOnlyMode}
                  disabled={isReadOnlyMode}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6}>
                <FormField
                  type="text"
                  label={t("resourceForm.code")}
                  name="code"
                  value={values.code || ""}
                  error={errors.code && touched.code}
                  readOnly={isReadOnlyMode}
                  disabled={isReadOnlyMode}
                />
              </Grid>
              <Grid item xs={12} id={"cy-defaultAppointmentProvider"}>
                {isReadOnlyMode ? (
                  <FormField
                    type="text"
                    label={t("appointmentForm.doctorProvider")}
                    name="defaultAppointmentProvider"
                    value={
                      model.defaultAppointmentProvider?.person
                        ? `${model.defaultAppointmentProvider.person.name} ${model.defaultAppointmentProvider.person.lastname}`
                        : ""
                    }
                    readOnly={true}
                    disabled={true}
                  />
                ) : (
                  <Autocomplete
                    defaultValue={
                      model.defaultAppointmentProvider?.person
                        ? {
                          id: model.defaultAppointmentProvider.id,
                          label:
                            model.defaultAppointmentProvider.person.name +
                            " " +
                            model.defaultAppointmentProvider.person.lastname,
                        }
                        : null
                    }
                    options={doctorOptions}
                    loading={isLoading}
                    name="defaultAppointmentProvider.id"
                    label={t("appointmentForm.doctorProvider")}
                    onChange={(_e, value) => {
                      if (value) {
                        setFieldValue("defaultAppointmentProvider.id", value.id);
                      }
                    }}
                    id="cy-defaultAppointmentProvideridFormContainer"
                    noOptionsText={t("resourceForm.noOptions")}
                  />
                )}
              </Grid>
              <Grid item xs={12} mb={3} id={"cy-location"}>
                {isReadOnlyMode ? (
                  <FormField
                    type="text"
                    label={t("resourceForm.location")}
                    name="location"
                    value={model.location?.name || ""}
                    readOnly={true}
                    disabled={true}
                  />
                ) : (
                  <Autocomplete
                    defaultValue={
                      model.location?.id
                        ? {
                          id: model.location.id,
                          label: model.location.name,
                        }
                        : null
                    }
                    options={facilityOptions}
                    loading={isLoading}
                    name="location.id"
                    label={t("resourceForm.location")}
                    onChange={(_e, value) => {
                      if (value) {
                        setFieldValue("location.id", value.id);
                      }
                    }}
                    noOptionsText={t("resourceForm.noOptions")}
                    id="cy-locationidFormContainer"
                  />
                )}
              </Grid>
              <Grid item xs={12} mb={5} lg={6}>
                {isReadOnlyMode ? (
                  <FormField
                    type="text"
                    label={t("facilityForm.phone")}
                    name="phone"
                    value={values.phone || ""}
                    readOnly={true}
                    disabled={true}
                  />
                ) : (
                  <FormPhoneInput
                    inputClass="phone"
                    specialLabel={t("facilityForm.phone")}
                    country={"us"}
                    required={true}
                    name="phone"
                    value={values.phone}
                    onChange={(value) => {
                      values.phone = value;
                    }}
                  />
                )}
              </Grid>
              <Grid container spacing={2} padding={2} alignItems="center">
                {!isReadOnlyMode && (
                  <Grid item xs={12} lg={6}>
                    {action === FormActionType.NEW ? (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].CREATE]}
                      >
                        <Button id="cy-submit-button" type="submit" variant="contained" color="info" disabled={isLoading}>
                          {t("stepperFormActionButtons.submit")}
                        </Button>
                      </PermissionWrapper>
                    ) : (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.RESOURCE].EDIT]}
                      >
                        <Button id="cy-update-button" type="submit" variant="contained" color="info" disabled={isLoading}>
                          {t("stepperFormActionButtons.update")}
                        </Button>
                      </PermissionWrapper>
                    )}
                  </Grid>
                )}
                <Grid item xs={12} lg={!isReadOnlyMode ? 6 : 12}>
                  <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </FormikForm>
    </Box>
  );
};

export default ResourceForm;
